/**
 * Stage team-shared shipping-label attaches into the print queue for a set of orders.
 */

import { getDriveShareLink } from '../services/operatorDriveService';
import {
  SHIPPING_LABEL_ATTACH_STATUS,
  buildOrderKeyToPagesMap,
} from '../services/shippingLabelAttachService';
import { loadOperatorDriveItemBlob } from './operatorDriveShareDownload';
import { stageMatchedShippingLabelPdfToQueue } from './stageMatchedShippingLabelPdfToQueue';
import { getWaveOrderKey } from './wavePickListModel';

async function loadAttachSourceBlob(attach, blobCache) {
  if (attach?.id && blobCache.has(attach.id)) return blobCache.get(attach.id);
  if (!attach?.driveItemId) {
    throw new Error(`Attach “${attach?.fileName || attach?.id}” has no Smoke Drive file link.`);
  }
  const { blob } = await loadOperatorDriveItemBlob(
    { id: attach.driveItemId, name: attach.fileName, type: 'file' },
    { resolveDownloadUrl: async (item) => getDriveShareLink(item.id) },
  );
  blobCache.set(attach.id, blob);
  return blob;
}

/**
 * @param {{
 *   attaches?: object[],
 *   orders: object[],
 *   workingDeliveryDate?: string,
 *   enqueueCarrierLabel: Function,
 * }} opts
 */
export async function stageTeamCarrierAttaches(opts) {
  const {
    attaches = [],
    orders = [],
    workingDeliveryDate = '',
    enqueueCarrierLabel,
  } = opts;
  const attached = (attaches || []).filter(
    (entry) => entry.status === SHIPPING_LABEL_ATTACH_STATUS.ATTACHED,
  );
  const scopedKeys = new Set(orders.map((order) => getWaveOrderKey(order)).filter(Boolean));
  const preferredOrderKeys = orders.map((order) => getWaveOrderKey(order)).filter(Boolean);
  const blobCache = new Map();
  let stagedLabels = 0;
  const missingOrders = [];

  for (const attach of attached) {
    const orderPages = buildOrderKeyToPagesMap(attach);
    const assignments = [];
    orderPages.forEach((pageNumbers, orderKey) => {
      if (!scopedKeys.has(orderKey)) return;
      pageNumbers.forEach((pageNumber) => {
        assignments.push({ pageNumber, orderKey });
      });
    });
    if (!assignments.length) continue;
    const sourceBlob = await loadAttachSourceBlob(attach, blobCache);
    const pageMatches = assignments.map((assignment) => ({
      pageNumber: assignment.pageNumber,
      suggestedOrderKey: assignment.orderKey,
      status: 'matched',
      matches: [],
    }));
    const pageAssignments = Object.fromEntries(
      assignments.map((assignment) => [assignment.pageNumber, assignment.orderKey]),
    );
    const staged = await stageMatchedShippingLabelPdfToQueue({
      sourceBlob,
      pageMatches,
      pageAssignments,
      candidateOrders: orders,
      workingDeliveryDate,
      preferredOrderKeys,
      copies: 1,
      enqueueCarrierLabel,
    });
    stagedLabels += staged.orderCount;
  }

  scopedKeys.forEach((orderKey) => {
    const has = attached.some((attach) =>
      (attach.pages || []).some((page) => String(page.orderKey) === orderKey),
    );
    if (!has) missingOrders.push(orderKey);
  });

  return {
    stagedLabels,
    attachCount: attached.length,
    missingOrderKeys: missingOrders,
  };
}
